import { wixUpgradeUrl } from '../utils/wixBilling';
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { navigateExternal } from '../utils/navigation.js';
import { canWrite } from '../utils/permissions';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext.js';

/**
 * Centralised upgrade / billing entry point.
 *
 * Usage:
 *   const { handleUpgradeClick, UpgradeModal, isUpgradeLoading } = useUpgrade();
 *
 *   // trigger:  handleUpgradeClick(bot)
 *   // render :  {UpgradeModal}   ← kept for call-site compatibility (now null)
 *
 * Routing rules:
 *   - Wix-installed bot (and no active Stripe sub) → redirect to the Wix App
 *     Market upgrade page (unchanged).
 *   - Every other bot → navigate to the per-agent billing page
 *     `/billing/:botId`, where the pricing tiers (or, for Wix bots, the
 *     "managed by Wix" view) are shown. The old in-app SubscriptionModal is no
 *     longer opened from here.
 *
 * An optional `{ onComplete }` arg is still accepted at call sites for backward
 * compatibility (it's simply ignored now — plan changes happen on the billing
 * page / Stripe-hosted surfaces, after which the bot context refetches itself).
 */
export default function useUpgrade() {
  const navigate = useNavigate();
  const { requestUnsavedAction } = useUnsavedChanges();
  const [wixLoading, setWixLoading] = useState(null); // bot.id while fetching Wix status

  const handleUpgradeClick = useCallback(
    async (bot) => {
      if (!bot) return;

      // Read-only teammates can't start/modify a subscription. Surface the
      // same unified "permission denied" modal that backend 403s trigger.
      if (!canWrite()) {
        window.dispatchEvent(
          new CustomEvent('ub:permission_denied', { detail: { message: '' } })
        );
        return;
      }

      const BASE = process.env.REACT_APP_API_BASE_URL;

      // Wix path — prioritise Wix unless there's already an active Stripe sub.
      const hasActiveStripe =
        bot?.subscription?.stripe_customer_id &&
        (bot?.subscription?.status === 'active' ||
          bot?.subscription?.status === 'trialing');

      // Key on wix_instance_id, not the stored refresh token: the token can be
      // null on a live install, and wix/status mints its own token from the
      // instance id (client_credentials) so it confirms the install regardless.
      if ((bot?.wix_instance_id || bot?.wix_product) && !hasActiveStripe) {
        setWixLoading(bot.id);
        try {
          const res = await fetch(
            `${BASE}wix/status?bot_id=${encodeURIComponent(bot.id)}`
          );
          const data = await res.json();
          if (data?.instance_id) {
            if (bot?.subscription_plan?.name === 'Free' && window.dataLayer) {
              window.dataLayer.push({ event: 'checkout_session_started' });
            }
            navigateExternal(wixUpgradeUrl(data));
            return;
          }
          if (data?.site_id) {
            navigateExternal(`https://manage.wix.com/dashboard/${data.site_id}/manage-website/plans`);
            return;
          }
        } catch (err) {
          console.error('Wix status check failed – falling back to billing page:', err);
        } finally {
          setWixLoading(null);
        }
        // No instance id (or error): fall through to the in-app billing page,
        // which renders the Wix-managed view for this bot.
      }

      // Shopify path (feature 29) — a Shopify-bound bot upgrades on Shopify's
      // hosted Managed-Pricing page (an ACTIVE Stripe sub already returned above
      // implicitly: hasActiveStripe bots skip both platform branches).
      if (!bot?.wix_instance_id && !bot?.wix_product && !hasActiveStripe) {
        try {
          const res = await fetch(
            `${BASE}apps/shopify/status?bot_id=${encodeURIComponent(bot.id)}`
          );
          const data = await res.json();
          if (res.ok && data?.connected && data?.links?.plan_url) {
            if (bot?.subscription_plan?.name === 'Free' && window.dataLayer) {
              window.dataLayer.push({ event: 'checkout_session_started' });
            }
            navigateExternal(data.links.plan_url);
            return;
          }
        } catch (err) {
          console.error('Shopify status check failed - falling back to billing page:', err);
        }
        // Not Shopify-bound (or error): fall through to the billing page.
      }

      // Everything else → the per-agent billing page (pricing tiers / Wix view).
      requestUnsavedAction(() => navigate(`/billing/${bot.id}`));
    },
    [navigate, requestUnsavedAction]
  );

  // Kept so existing `{UpgradeModal}` render slots stay valid; the modal is no
  // longer used (the billing page replaces it).
  const UpgradeModal = null;

  const isUpgradeLoading = useCallback(
    (botId) => wixLoading === botId,
    [wixLoading]
  );

  return { handleUpgradeClick, UpgradeModal, isUpgradeLoading };
}
